import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, Grid2, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { HelmetDexteel } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	DELAY_CATEGORY_INITIAL_VALUES,
	DelayCategory,
} from "../models/CatalogCategory";
import {
	deleteCatalogCategory,
	upsertCatalogCategory,
} from "../repositories/CatalogCategoriesRepository";
import ColorPicker from "./ColorPicker/ColorPicker";

type Props = {
	delayCategory: DelayCategory | null;
	modal: "create" | "update" | "delete" | "";
	delayAreaAssetId: number | null;
	onHide: (shouldUpdate: boolean) => void;
};

export const UpsertDeleteModal = ({
	delayCategory,
	modal,
	delayAreaAssetId,
	onHide,
}: Props) => {
	const [title, setTitle] = useState<string>("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const succesfullyMessage = `The category was ${modal}d succesfully`;

	const { control, reset, handleSubmit } = useForm<DelayCategory>({});

	const validateNoSpecialChars = (value: string) =>
		/^[a-zA-Z0-9 _-]*$/.test(value) || "No special characters";

	const queryClient = useQueryClient();
	const deleteMutation = useMutation(deleteCatalogCategory, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["delayCategories"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});
	const upsertMutation = useMutation(upsertCatalogCategory, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["delayCategories"]);
			setNotificationMessage(succesfullyMessage);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<DelayCategory> = async (
		data: DelayCategory,
	) => {
		if (modal === "delete") {
			deleteMutation.mutate(data.DelayCategoryId);
		} else {
			upsertMutation.mutate(data);
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(
				modal === "create"
					? {
							...DELAY_CATEGORY_INITIAL_VALUES,
							DelayAreaAssetId: delayAreaAssetId,
						}
					: delayCategory || DELAY_CATEGORY_INITIAL_VALUES,
			);
			setTitle(
				modal === "create" ? "CREATE" : modal === "update" ? "EDIT" : "DELETE",
			);
			setIsSubmitLoading(false);
		}
	}, [modal, delayCategory, delayAreaAssetId]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Delay Category - Delay Categories`}
				/>
			)}
			<MesfModal
				id="modal-category"
				title={`${title} CATEGORY`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="md"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content style={{ padding: "15px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="DelayAreaAssetId"
									control={control}
									rules={{ required: "Delay Area is required" }}
									render={({ field, fieldState: { error } }) => (
										<DelayAreaSelector
											label="Delay Area"
											value={field.value}
											onChange={field.onChange}
											fieldError={error}
											disabled={modal === "delete" || modal === "update"}
											required
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Controller
									name="DelayCategoryName"
									control={control}
									rules={{
										required: "Name is required",
										validate: validateNoSpecialChars,
									}}
									render={({ field, fieldState: { error } }) => (
										<TextField
											label="Name"
											variant="outlined"
											value={field.value}
											onChange={field.onChange}
											error={!!error}
											helperText={error?.message}
											fullWidth
											autoComplete="off"
											size="small"
											disabled={modal === "delete"}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 3, md: 3 }} style={{ marginTop: 10 }}>
								<Controller
									name="DelayCategoryColor"
									control={control}
									rules={{ required: "Color is required" }}
									render={({ field, fieldState: { error } }) => (
										<>
											<ColorPicker
												onChange={(_: string, color: string) =>
													field.onChange(color)
												}
												color={field.value}
												disabled={modal === "delete"}
												label="Color"
											/>
											{error && (
												<span
													style={{
														fontSize: 12,
														color: "#F44336",
													}}
												>
													{error?.message}
												</span>
											)}
										</>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 3, md: 3 }}>
								<Controller
									name="IsActive"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											style={{ userSelect: "none" }}
											control={
												<Checkbox
													checked={field.value}
													onChange={field.onChange}
													name="IsActive"
													color="primary"
												/>
											}
											label="Is Active"
											disabled={modal === "delete"}
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={onHide}
							buttonLabel={modal === "delete" ? "Delete" : "Save"}
							buttonColor={modal === "delete" ? "secondary" : "primary"}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
